import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { getQuery } from 'sutils';
import { createContentStatusManager } from '../core/factory/content-status-manager-factory';
import { ILogger, SlideDataServices } from '../core/slide-data-services.core';
import { registerLocalEditSlideDataServices } from '../scenes/local-edit/entry/local-edit.entry';
import { registerOnlineSlideDataServices } from '../scenes/online/entry/online.entry';
import { registerSubsidiarySlideDataServices } from '../scenes/subsidiary/entry/subsidiary.entry';
import { SlideCountState } from '../store/slide-count-state';
import { SlideCountManager } from '../core/managers/slide-count-manager';
import { MessageProtocol } from '@src/slide/infrastructure/module/independent/windows-message-protocol';
import { MainWindowDataProviderService } from '../core/services/main-window-data-provider-service';
import { ColorManager, CommandManager, ObserverManager, ResourceMapManager, SearchManager, TransactionManager, EventManager, MutationManager, DataHelper } from '@tencent/slide-sdk-model';
import { callable } from '@tencent/tiny-ioc';
import { PresentationStoreService } from '../core/services/presentation-store-service';
import { PrivilegeManager } from '../core/managers/privilege-manager';
import { createNanoEvents } from 'nanoevents';
import {
    tdw,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { reportErrorCode as tdrec } from '@tencent/tencent-doc-report';
import {
    statusReport007,
    valueReport007,
} from '@src/slide/infrastructure/module/dependent/report/real-time-report/real-time';
import AegisWrapper from '@src/slide/infrastructure/module/dependent/report/platform/aegis';
import { OperationLogger } from '@tencent/slide-sdk-devtools';
import { SnapshotManager } from '@tencent/slide-sdk-persistent';
import { SlideFileManager } from '../core/managers/slide-file-manager';
import { isSyncManagerEnabled } from '../scenes/offline/utils';


function registerSlideAppServices() {
    // 注册主副窗口文档数据服务
    MessageProtocol.getInstance().registerService(MainWindowDataProviderService);

    SlideDataServices.Infra.Logger.register(() => {
        const logger: ILogger = {
            info: (...args) => window.log.info(...args),
            error: (...args) => window.log.error(...args),
            warn: (...args) => window.log.warn(...args),
            log: (...args) => window.log.log(...args),
            debug: (...args) => window.log.debug(...args),
            // websocket错误上报用
            tdrec,
            tdw,
            monitor: {
                reportValue: valueReport007,
                reportStatus: statusReport007,
            },
            getPrototype: () => window.log,
        };
        if (ENV.IS_RELEASE) {
            logger.aegis = AegisWrapper.getInstance().aegis;
        }
        return logger;
    });

    SlideDataServices.Infra.Mqq.register(() => {
        const hasMqq = typeof window && typeof window.mqq?.invoke === 'function';
        return hasMqq ? window.mqq : { invoke: () => {} };
    });

    SlideDataServices.States.SlideCountState.register(() => {
        const counts = new SlideCountState();
        SlideCountManager.register(counts);
        return counts;
    });


    SlideDataServices.UI.Snackbar.register(async () => {
        const { Snackbar } = await (browser.isMobile
            ? import('@tencent/dui-mobile/lib/components/Snackbar')
            : import('@tencent/dui/lib/components/Snackbar'));
        return Snackbar;
    });

    SlideDataServices.UI.Modal.register(async () => {
        const { Modal } = await (browser.isMobile
            ? import('@tencent/dui-mobile/lib/components/Modal')
            : import('@tencent/dui/lib/components/Modal'));
        return Modal;
    });

    SlideDataServices.EventManager.register(() => createNanoEvents());
    SlideDataServices.UIEventManager.register(() => createNanoEvents());
    SlideDataServices.PrivilegeManager.register(callable(PrivilegeManager));
    SlideDataServices.ContentStatusManager.register(createContentStatusManager);
    if (ENV.IS_TEST || __IS_DEV__) {
        SlideDataServices.OperationLogger.register(callable(OperationLogger));
    } else {
        SlideDataServices.OperationLogger.register(() => ({} as unknown as OperationLogger));
    }
    SlideDataServices.SDKDataServices.EventManager.register(callable(EventManager));
    SlideDataServices.SDKDataServices.MutationManager.register((eventManager, logger) => new MutationManager(
        eventManager,
        logger,
        () => SlideDataServices.DocInfoManager() ?? {},
    ));
    SlideDataServices.SDKDataServices.PresentationStoreService.register((...params) => new PresentationStoreService(
        ...params,
        SlideDataServices.OperationLogger(),
    ));
    SlideDataServices.SDKDataServices.PresentationStoreService.promise().then((presentationStoreService) => {
        const presentationDataModel = presentationStoreService.getPresentation();
        SlideDataServices.SDKDataServices.SearchManager
            .register(eventManager => new SearchManager(presentationDataModel, eventManager));
    });
    SlideDataServices.SDKDataServices.TransactionManager.register(callable(TransactionManager));
    SlideDataServices.SDKDataServices.ObserverManager.register(callable(ObserverManager));
    SlideDataServices.SDKDataServices.ColorManager.register(callable(ColorManager));
    SlideDataServices.SDKDataServices.DataHelper.register(callable(DataHelper));
    SlideDataServices.SDKDataServices.ResourceMapManager.register(callable(ResourceMapManager));
    SlideDataServices.SDKDataServices.SnapshotManager.register((
        presentationStoreService,
        ...params
    ) => new SnapshotManager(
        { getSnapshotData: () => presentationStoreService.getPresentationSnapShotData() },
        ...params,
    ));

    SlideDataServices.SlideFileManager.register(callable(SlideFileManager));

    const isSubsidiary = getQuery('isSubsidiary') === '1';
    if (isSubsidiary) {
        SlideDataServices.SDKDataServices.CommandManager.register(callable(CommandManager));
        registerSubsidiarySlideDataServices();
        return;
    }

    if (isSyncManagerEnabled()) {
        import('../core/managers/sync-manager/online-sync-manager').then(({ OnlineSyncManager }) => {
            SlideDataServices.SyncManager.register(callable(OnlineSyncManager));
        });
    }

    // import('../core/managers/sync-manager/local-sync-manager').then(({ LocalSyncManager }) => {
    //     SlideDataServices.SyncManager.register(callable(LocalSyncManager));
    // });

    if (ENV.IS_LOCAL_EDIT) {
        SlideDataServices.SDKDataServices.CommandManager.register(callable(CommandManager));
        registerLocalEditSlideDataServices();
        return;
    }
    registerOnlineSlideDataServices();
}

registerSlideAppServices();
